import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import { CheckBox } from "./CheckBox";
import { useInfiniteScroll } from "@/hooks/components/useInfiniteScroll";
import ContextMenu, { ContextMenuItem } from "./ContextMenu";

export interface TableColumn {
  key: string;
  header: string;
  width?: string;
  minWidth?: string; // 최소 너비
  maxWidth?: string; // 최대 너비
  headerAlign?: "left" | "center" | "right"; // 헤더 정렬
  bodyAlign?: "left" | "center" | "right";   // 바디 정렬
  render?: (value: any, row: any, index: number) => React.ReactNode;
  sortable?: boolean;
  group?: string; // 그룹 헤더명
  resizable?: boolean; // 크기 조절 가능 여부
  sticky?: "left" | "right"; // 세로 스크롤 시 컬럼 고정 (왼쪽 또는 오른쪽)
}

export interface TableProps {
  columns: TableColumn[];
  data: any[];
  showCheckbox?: boolean;
  showRowNumber?: boolean;
  selectedRows?: number[];
  onRowSelect?: (selectedRows: number[]) => void;
  onRowClick?: (row: any, index: number) => void;
  onRowDoubleClick?: (row: any, index: number) => void;
  className?: string;
  rowClassName?: (row: any, index: number) => string;
  rowTextColor?: (row: any, index: number) => string | null; // 행별 텍스트 색상 조건
  maxHeight?: string;
  minWidth?: string; // 테이블 최소 너비 설정
  loading?: boolean;
  emptyMessage?: string;
  resizable?: boolean; // 컬럼 크기 조절 기능 활성화
  // 무한 스크롤 관련 props
  infiniteScroll?: boolean;
  initialLoadCount?: number;
  loadMoreCount?: number;
  hasMore?: boolean;
  onLoadMore?: (currentCount: number) => void;
  loadingMore?: boolean;
  // 페이지네이션 관련 props 추가
  enablePagination?: boolean;
  pageSize?: number;
  onPageChange?: (page: number) => void;
  currentPage?: number;
  // 컨텍스트 메뉴 관련 props
  contextMenuItems?: (row: any, index: number) => ContextMenuItem[];
  onContextMenu?: (row: any, index: number, event: React.MouseEvent) => void;
  // 행 클릭 배경색 변경 기능
  highlightOnClick?: boolean;
  // sticky 컬럼 관련 props
  stickyRowNumber?: boolean; // 번호 컬럼 고정 여부
  stickyCheckbox?: boolean; // 체크박스 컬럼 고정 여부
  getCellColSpan?: (row: any, column: TableColumn, colIndex: number) => number | 0;
  getRowBgClassName?: (row: any, index: number) => string | null;
}

export default function Table({
  columns,
  data,
  showCheckbox = false,
  showRowNumber = false,
  selectedRows = [],
  onRowSelect,
  onRowClick,
  onRowDoubleClick,
  className = "",
  rowClassName,
  rowTextColor,
  maxHeight = "600px",
  loading = false,
  emptyMessage = "데이터가 없습니다.",
  resizable = false,
  // 무한 스크롤 기본값
  infiniteScroll = false,
  initialLoadCount = 20,
  loadMoreCount = 20,
  hasMore,
  onLoadMore,
  loadingMore,
  // 페이지네이션 기본값
  enablePagination = false,
  pageSize = 20,
  onPageChange,
  currentPage = 1,
  // 컨텍스트 메뉴 기본값
  contextMenuItems,
  onContextMenu,
  // 행 클릭 하이라이트 기본값
  highlightOnClick = false,
  // sticky 컬럼 기본값
  stickyRowNumber = false,
  stickyCheckbox = false,
  getCellColSpan,
  getRowBgClassName,
}: TableProps) {
  const [allSelected, setAllSelected] = useState(false);
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const rowNumThRef = useRef<HTMLTableCellElement | null>(null);
  const checkboxThRef = useRef<HTMLTableCellElement | null>(null);
  const headerRefs = useRef<Record<string, HTMLTableCellElement | null>>({});
  const [leftOffsets, setLeftOffsets] = useState<number[]>([]);
  const [rightOffsets, setRightOffsets] = useState<number[]>([]);

  // 컨텍스트 메뉴 상태
  const [contextMenu, setContextMenu] = useState<{
    isOpen: boolean;
    x: number;
    y: number;
    row: any;
    index: number;
  }>({
    isOpen: false,
    x: 0,
    y: 0,
    row: null,
    index: -1,
  });

  // 우클릭한 행 인덱스 상태
  const [rightClickedRowIndex, setRightClickedRowIndex] = useState<number | null>(null);

  // 클릭으로 하이라이트된 행 인덱스 상태
  const [clickedRowIndex, setClickedRowIndex] = useState<number | null>(null);

  // 컬럼 너비 상태
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});

  // 리사이즈 상태
  const [resizeState, setResizeState] = useState<{
    isResizing: boolean;
    columnKey: string;
    startX: number;
    startWidth: number;
  } | null>(null);

  // 무한 스크롤 hook 사용
  const infiniteScrollResult = useInfiniteScroll({
    data,
    initialLoadCount,
    loadMoreCount,
    onLoadMore,
    tableContainerRef,
    hasMore, // hasMore prop 전달
    enablePagination,
    pageSize,
    onPageChange,
    currentPage,
  });

  // 무한 스크롤이 활성화된 경우 hook의 데이터 사용, 아니면 전체 데이터 사용
  const actualData = infiniteScroll ? infiniteScrollResult.displayedData : data;
  const actualHasMore = infiniteScroll ? infiniteScrollResult.hasMore : !!hasMore;
  const actualLoadingMore = infiniteScroll ? infiniteScrollResult.loadingMore : !!loadingMore;

  // 외부에서 전달된 상태와 hook의 상태 동기화
  useEffect(() => {
    if (infiniteScroll && typeof hasMore === "boolean") {
      infiniteScrollResult.setHasMore(hasMore);
    }
  }, [hasMore, infiniteScroll, infiniteScrollResult]);

  // 전체 행 선택/해제 핸들러
  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newAllSelected = e.target.checked;
    setAllSelected(newAllSelected);

    if (onRowSelect) {
      if (newAllSelected) {
        // 무한 스크롤인 경우 현재 표시된 데이터만 선택
        const dataToSelect = actualData;
        onRowSelect(dataToSelect.map((_, index) => index));
      } else {
        onRowSelect([]);
      }
    }
  };

  // left/right sticky 위치 재계산 (리사이즈/폰트 로드/컬럼 변경 시)
  useLayoutEffect(() => {
    // 0) 컬럼 교체 시 ref 맵 정리: 현재 columns의 키만 유지(안전)
    {
      const curr = headerRefs.current;
      const next: Record<string, HTMLTableCellElement | null> = {};
      columns.forEach(c => {
        next[c.key] = curr[c.key] ?? null;
      });
      headerRefs.current = next;
    }

    const calc = () => {
      // 왼쪽 기준(번호/체크박스) 실제 폭 합산
      const rn = stickyRowNumber ? (rowNumThRef.current?.getBoundingClientRect().width ?? 0) : 0;
      const cb = showCheckbox && stickyCheckbox ? (checkboxThRef.current?.getBoundingClientRect().width ?? 0) : 0;
      const baseLeft = rn + cb;

      // 1) leftOffsets
      const lo: number[] = new Array(columns.length).fill(0);
      let leftSum = baseLeft;
      columns.forEach((col, i) => {
        if (col.sticky === "left") {
          lo[i] = leftSum;
          const th = headerRefs.current[col.key];
          const w = th?.getBoundingClientRect().width ?? 0;
          leftSum += w;
        } else {
          lo[i] = 0;
        }
      });
      setLeftOffsets(lo);

      // 2) rightOffsets
      const ro: number[] = new Array(columns.length).fill(0);
      let rightSum = 0;
      for (let i = columns.length - 1; i >= 0; i--) {
        const col = columns[i];
        if (col.sticky === "right") {
          ro[i] = rightSum;
          const th = headerRefs.current[col.key];
          const w = th?.getBoundingClientRect().width ?? 0;
          rightSum += w;
        } else {
          ro[i] = 0;
        }
      }
      setRightOffsets(ro);
    };

    // 1) 초기 계산: 레이아웃 안정 직후 한 프레임 미뤄서 측정
    let rafId = requestAnimationFrame(calc);

    // 2) 리사이즈 옵저버
    const ros: ResizeObserver[] = [];
    const observe = (el: Element | null | undefined) => {
      if (!el) return;
      const ro = new ResizeObserver(() => calc());
      ro.observe(el);
      ros.push(ro);
    };

    observe(rowNumThRef.current);
    observe(checkboxThRef.current);
    columns.forEach(col => observe(headerRefs.current[col.key]));

    // 3) 윈도우 이벤트
    const onLoadOrResize = () => requestAnimationFrame(calc);
    window.addEventListener("load", onLoadOrResize);
    window.addEventListener("resize", onLoadOrResize);

    // 4) 폰트 로드 완료 시 재계산
    let fontsCancelled = false;
    if ("fonts" in document) {
      (document as any).fonts?.ready?.then(() => {
        if (!fontsCancelled) calc();
      });
    }

    return () => {
      cancelAnimationFrame(rafId);
      ros.forEach(ro => ro.disconnect());
      window.removeEventListener("load", onLoadOrResize);
      window.removeEventListener("resize", onLoadOrResize);
      fontsCancelled = true;
    };
  }, [columns, showCheckbox, stickyCheckbox, stickyRowNumber]);

  // 선택한 행 핸들러
  const handleRowSelect = (index: number) => {
    if (onRowSelect) {
      const newSelectedRows = selectedRows.includes(index)
        ? selectedRows.filter(i => i !== index)
        : [...selectedRows, index];
      onRowSelect(newSelectedRows);

      // 현재 표시된 데이터 기준으로 전체 선택 상태 업데이트
      setAllSelected(newSelectedRows.length === actualData.length && actualData.length > 0);
    }
  };

  // 컨텍스트 메뉴(우클릭) 핸들러
  const handleContextMenu = (row: any, index: number, event: React.MouseEvent) => {
    const hasCustomMenu = Boolean(contextMenuItems || onContextMenu);

    // 컨텍스트 메뉴를 쓰지 않으면 리턴
    if (!hasCustomMenu) return;

    // 커스텀 메뉴가 있을 때만 기본 메뉴 막고, 하이라이트/메뉴 오픈
    event.preventDefault();

    // 우클릭한 행 인덱스 설정
    setRightClickedRowIndex(index);

    // 외부 onContextMenu 콜백 호출
    onContextMenu?.(row, index, event);

    // 커스텀 컨텍스트 메뉴 아이템이 있는 경우 표시
    if (contextMenuItems) {
      setContextMenu({
        isOpen: true,
        x: event.clientX,
        y: event.clientY,
        row,
        index,
      });
    }
  };

  // 컨텍스트 메뉴(우클릭) 닫기 핸들러
  const closeContextMenu = () => {
    setContextMenu({
      isOpen: false,
      x: 0,
      y: 0,
      row: null,
      index: -1,
    });
    // 컨텍스트 메뉴가 닫힐 때 우클릭한 행 인덱스 초기화
    setRightClickedRowIndex(null);
  };

  // 컬럼 리사이즈 시작 핸들러
  const handleResizeStart = (e: React.MouseEvent, columnKey: string, thElement: HTMLElement) => {
    e.preventDefault();
    e.stopPropagation();

    // 실제 렌더링된 너비 측정
    const actualWidth = thElement.getBoundingClientRect().width;

    setResizeState({
      isResizing: true,
      columnKey,
      startX: e.clientX,
      startWidth: actualWidth,
    });
  };

  // 컬럼 리사이즈 중 핸들러
  const handleResizeMove = (e: MouseEvent) => {
    if (!resizeState) return;

    const deltaX = e.clientX - resizeState.startX;
    const newWidth = Math.max(50, resizeState.startWidth + deltaX); // 최소 50px

    setColumnWidths(prev => ({
      ...prev,
      [resizeState.columnKey]: newWidth,
    }));
  };

  // 컬럼 리사이즈 종료 핸들러
  const handleResizeEnd = () => {
    setResizeState(null);
  };

  // 컬럼 너비 스타일 가져오기
  const getColumnWidthStyle = (column: TableColumn): React.CSSProperties => {
    const style: React.CSSProperties = {};
    if (columnWidths[column.key]) {
      style.width = `${columnWidths[column.key]}px`;
    } else if (column.width) {
      style.width = column.width;
    }
    if (column.minWidth) {
      style.minWidth = column.minWidth;
    }
    if (column.maxWidth) {
      style.maxWidth = column.maxWidth;
    }
    return style;
  };

  // 컬럼 너비 가져오기 (기존 호환성 유지)
  const getColumnWidth = (column: TableColumn) => {
    if (columnWidths[column.key]) {
      return `${columnWidths[column.key]}px`;
    }
    return column.width || "auto";
  };

  // 마우스 이벤트 리스너 추가/제거
  useEffect(() => {
    if (resizeState?.isResizing) {
      document.addEventListener("mousemove", handleResizeMove);
      document.addEventListener("mouseup", handleResizeEnd);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";

      return () => {
        document.removeEventListener("mousemove", handleResizeMove);
        document.removeEventListener("mouseup", handleResizeEnd);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
      };
    }
  }, [resizeState]);

  // 헤더 전용 align
  const getHeaderAlignClass = (align?: string) => {
    switch (align) {
      case "left":
        return "text-left";
      case "right":
        return "text-right";
      default:
        return "text-center";
    }
  };

  // 바디 전용 align
  const getBodyAlignClass = (align?: string) => {
    switch (align) {
      case "left":
        return "text-left";
      case "right":
        return "text-right";
      default:
        return "text-center";
    }
  };

  // sticky 컬럼의 left/right 위치 계산 (순차적 배치)
  const getStickyPosition = (columnIndex: number, sticky?: "left" | "right") => {
    if (!sticky) return {};
    if (sticky === "left") {
      return { left: `${leftOffsets[columnIndex] || 0}px`, top: "0px" };
    }
    // 오른쪽 sticky: 오른쪽에서부터 실측 누적 오프셋 사용
    return { right: `${rightOffsets[columnIndex] || 0}px`, top: "0px" };
  };

  // sticky 클래스 생성 (기존 레이아웃 유지)
  const getStickyClass = (
    sticky?: "left" | "right",
    isHeader?: boolean,
    backgroundColor?: string,
    borderClass?: string
  ) => {
    if (!sticky) return "";
    const bgClass = isHeader ? "bg-[#D0E7AC]" : backgroundColor || "bg-white";
    const borders = borderClass || "";
    return `sticky z-40 ${bgClass} ${borders}`;
  };

  // 마지막 left-sticky 컬럼인지 확인
  const isLeftStickyBoundary = (i: number) => {
    const cur = columns[i];
    const next = columns[i + 1];
    return cur.sticky === "left" && (!next || next.sticky !== "left");
  };

  // 마지막 right-sticky 컬럼인지 확인
  const isRightStickyBoundary = (i: number) => {
    const cur = columns[i];
    const prev = columns[i - 1];
    return cur.sticky === "right" && (!prev || prev.sticky !== "right");
  };

  // resizable 여부에 따라 relative 적용
  const needRelative = (col: TableColumn) => (resizable && col.resizable !== false && !col.sticky ? "relative" : "");

  // 총 컬럼 개수 계산
  const totalColumns = columns.length + (showCheckbox ? 1 : 0) + (showRowNumber ? 1 : 0);

  // 헤더 border 클래스 생성
  const getHeaderBorderClass = (index: number) => {
    if (index === 0) return ""; // 첫 번째 컬럼은 왼쪽 border 없음
    if (index === totalColumns - 1) return "border-l border-l-[#81B334]"; // 마지막 컬럼은 오른쪽 border 없음
    return "border-l border-l-[#81B334]";
  };

  // 바디 border 클래스 생성
  const getBodyBorderClass = (index: number) => {
    if (index === 0) return ""; // 첫 번째 컬럼은 왼쪽 border 없음
    if (index === totalColumns - 1) return "border-l border-l-[#EBEBEB]"; // 마지막 컬럼은 오른쪽 border 없음
    return "border-l border-l-[#EBEBEB]";
  };

  // allSelected 상태를 selectedRows와 동기화
  useEffect(() => {
    setAllSelected(selectedRows.length === actualData.length && actualData.length > 0);
  }, [selectedRows, actualData.length]);

  // 그룹 헤더 생성 로직 - 원래 순서 유지
  const createGroupHeaders = () => {
    const groupMap = new Map<string, { columns: TableColumn[]; startIndex: number }>();
    const columnInfo: Array<{ column: TableColumn; groupName?: string; groupIndex?: number }> = [];

    // 컬럼들을 순서대로 처리하면서 그룹 정보 수집
    columns.forEach((column, index) => {
      if (column.group) {
        if (!groupMap.has(column.group)) {
          groupMap.set(column.group, { columns: [], startIndex: index });
        }
        groupMap.get(column.group)!.columns.push(column);
        columnInfo.push({
          column,
          groupName: column.group,
          groupIndex: groupMap.get(column.group)!.columns.length - 1,
        });
      } else {
        columnInfo.push({ column });
      }
    });

    return { groupMap, columnInfo };
  };

  const { groupMap, columnInfo } = createGroupHeaders();
  const hasGroupHeaders = groupMap.size > 0;

  const ROW_BG_SELECTED = "bg-[#CBD1D7]"; // 선택/우클릭/클릭 시 셀 색상
  const ROW_BG_HOVER_TD = "group-hover:bg-[#EEF2F7]"; // 행 hover 시 셀 색상 (스티키 포함)

  // 헤더 높이 통일
  const headerPyClass = hasGroupHeaders ? "py-2" : "py-6"; // 일반 헤더(th)
  const headerRowSpan2PyClass = hasGroupHeaders ? "py-2" : "py-6"; // rowSpan=2 헤더(th)

  // 테이블 셀 정렬용 클래스 반환 (기본: center)
  const getAlignClasses = (align?: "left" | "center" | "right") => {
    switch (align) {
      case "left":
        return "justify-start text-left";
      case "right":
        return "justify-end text-right";
      case "center":
      default:
        return "justify-center text-center";
    }
  };

  return (
    <div className={`overflow-hidden rounded-sm ${className}`}>
      <div
        ref={tableContainerRef}
        className="relative overflow-x-auto overflow-y-auto table-fixed-layout"
        style={{ maxHeight }}
      >
        <table className="min-w-full w-max">
          <thead className="bg-[#D0E7AC] sticky top-0 z-50">
            {/* 그룹 헤더 행 */}
            {hasGroupHeaders && (
              <tr>
                {showRowNumber && (
                  <th
                    ref={rowNumThRef}
                    rowSpan={2}
                    className={`w-16 px-3 ${headerRowSpan2PyClass} text-center font-medium text-gray-700 ${
                      stickyRowNumber
                        ? getStickyClass("left", true, undefined, getHeaderBorderClass(0))
                        : getHeaderBorderClass(0)
                    }`}
                    style={stickyRowNumber ? { left: "0px", top: "0px" } : {}}
                  >
                    번호
                  </th>
                )}
                {showCheckbox && (
                  <th
                    ref={checkboxThRef}
                    rowSpan={2}
                    className={`w-12 px-3 ${headerRowSpan2PyClass} ${
                      stickyCheckbox
                        ? getStickyClass("left", true, undefined, getHeaderBorderClass(showRowNumber ? 1 : 0))
                        : getHeaderBorderClass(showRowNumber ? 1 : 0)
                    }`}
                    style={
                      stickyCheckbox
                        ? {
                            left: `${stickyRowNumber ? (rowNumThRef.current?.getBoundingClientRect().width ?? 0) : 0}px`,
                            top: "0px",
                          }
                        : {}
                    }
                  >
                    <div className="flex justify-center items-center">
                      <CheckBox checked={allSelected} onChange={handleSelectAll} />
                    </div>
                  </th>
                )}

                {/* 컬럼을 원래 순서대로 처리 */}
                {(() => {
                  let currentIndex = (showRowNumber ? 1 : 0) + (showCheckbox ? 1 : 0);
                  const renderedGroups = new Set<string>();

                  return columnInfo
                    .map((info, colIndex) => {
                      const { column, groupName } = info;

                      if (!groupName) {
                        // 그룹이 없는 컬럼
                        const stickyStyle = getStickyPosition(colIndex, column.sticky);
                        const result = (
                          <th
                            key={column.key}
                            ref={el => {
                              headerRefs.current[column.key] = el;
                            }}
                            data-column-key={column.key}
                            rowSpan={2}
                            className={[
                              `px-3 ${headerRowSpan2PyClass} font-medium text-gray-700 whitespace-pre-line`,
                              getHeaderAlignClass(column.headerAlign),
                              column.sticky
                                ? getStickyClass(column.sticky, true, undefined, getHeaderBorderClass(currentIndex))
                                : getHeaderBorderClass(currentIndex),
                              column.sticky ? "sticky" : "",
                              needRelative(column),
                              // 경계선: 왼쪽 스티키의 마지막이면 오른쪽 두께선, 오른쪽 스티키의 마지막이면 왼쪽 두께선
                              isLeftStickyBoundary(colIndex) ? "sticky-edge-right" : "",
                              isRightStickyBoundary(colIndex) ? "sticky-edge-left" : "",
                            ].join(" ")}
                            style={{ ...getColumnWidthStyle(column), ...stickyStyle }}
                          >
                            {column.header}
                            {resizable && column.resizable !== false && (
                              <div
                                className="resize-handle absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-[#81B334] hover:w-1 z-50 select-none"
                                onMouseDown={e => {
                                  const thElement = e.currentTarget.parentElement as HTMLElement;
                                  handleResizeStart(e, column.key, thElement);
                                }}
                              />
                            )}
                          </th>
                        );
                        currentIndex++;
                        return result;
                      } else if (!renderedGroups.has(groupName)) {
                        // 그룹 헤더 (첫 번째 컬럼일 때만)
                        renderedGroups.add(groupName);
                        const groupColumns = groupMap.get(groupName)!.columns;
                        const result = (
                          <th
                            key={groupName}
                            colSpan={groupColumns.length}
                            className={`px-3 ${headerPyClass} font-medium text-gray-700 whitespace-pre-line text-center border-b border-b-[#81B334] ${getHeaderBorderClass(currentIndex)}`}
                          >
                            {groupName}
                          </th>
                        );
                        currentIndex += groupColumns.length;
                        return result;
                      }
                      return null;
                    })
                    .filter(Boolean);
                })()}
              </tr>
            )}

            {/* 일반 헤더 행 */}
            <tr>
              {!hasGroupHeaders && showRowNumber && (
                <th
                  className={`w-16 px-3 ${headerRowSpan2PyClass} text-center font-medium text-gray-700 ${
                    stickyRowNumber
                      ? getStickyClass("left", true, undefined, getHeaderBorderClass(0))
                      : getHeaderBorderClass(0)
                  }`}
                  style={stickyRowNumber ? { left: "0px", top: "0px" } : {}}
                >
                  번호
                </th>
              )}
              {!hasGroupHeaders && showCheckbox && (
                <th
                  className={`w-12 px-3 ${headerRowSpan2PyClass} ${
                    stickyCheckbox
                      ? getStickyClass("left", true, undefined, getHeaderBorderClass(showRowNumber ? 1 : 0))
                      : getHeaderBorderClass(showRowNumber ? 1 : 0)
                  }`}
                  style={
                    stickyCheckbox
                      ? {
                          left: `${stickyRowNumber ? (rowNumThRef.current?.getBoundingClientRect().width ?? 0) : 0}px`,
                          top: "0px",
                        }
                      : {}
                  }
                >
                  <div className="flex justify-center items-center">
                    <CheckBox checked={allSelected} onChange={handleSelectAll} />
                  </div>
                </th>
              )}

              {!hasGroupHeaders &&
                columns.map((column, colIndex) => {
                  const currentIndex = (showRowNumber ? 1 : 0) + (showCheckbox ? 1 : 0) + colIndex;
                  const stickyStyle = getStickyPosition(colIndex, column.sticky);
                  return (
                    <th
                      key={column.key}
                      ref={el => {
                        headerRefs.current[column.key] = el;
                      }}
                      data-column-key={column.key}
                      className={[
                        `px-3 ${headerPyClass} font-medium text-gray-700 whitespace-pre-line`,
                        getHeaderAlignClass(column.headerAlign),
                        column.sticky
                          ? getStickyClass(column.sticky, true, undefined, getHeaderBorderClass(currentIndex))
                          : getHeaderBorderClass(currentIndex),
                        column.sticky ? "sticky" : "",
                        needRelative(column),
                        // 경계선: 왼쪽 스티키의 마지막이면 오른쪽 두께선, 오른쪽 스티키의 마지막이면 왼쪽 두께선
                        isLeftStickyBoundary(colIndex) ? "sticky-edge-right" : "",
                        isRightStickyBoundary(colIndex) ? "sticky-edge-left" : "",
                      ].join(" ")}
                      style={{ ...getColumnWidthStyle(column), ...stickyStyle }}
                    >
                      {column.header}
                      {resizable && column.resizable !== false && (
                        <div
                          className="resize-handle absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-[#81B334] hover:w-1 z-50 select-none"
                          onMouseDown={e => {
                            const thElement = e.currentTarget.parentElement as HTMLElement;
                            handleResizeStart(e, column.key, thElement);
                          }}
                        />
                      )}
                    </th>
                  );
                })}

              {hasGroupHeaders && (
                <>
                  {/* 그룹이 있는 경우 원래 순서대로 하위 헤더 렌더링 */}
                  {columnInfo.map((info, colIndex) => {
                    const { column, groupName } = info;
                    const currentIndex = (showRowNumber ? 1 : 0) + (showCheckbox ? 1 : 0) + colIndex;

                    // 그룹이 없는 컬럼은 이미 rowSpan=2로 처리됨
                    if (!groupName) {
                      return null;
                    }

                    const stickyStyle = getStickyPosition(colIndex, column.sticky);
                    return (
                      <th
                        key={column.key}
                        ref={el => {
                          headerRefs.current[column.key] = el;
                        }}
                        data-column-key={column.key}
                        className={[
                          `px-3 ${headerPyClass} font-medium text-gray-700 whitespace-pre-line`,
                          getHeaderAlignClass(column.headerAlign),
                          column.sticky
                            ? getStickyClass(column.sticky, true, undefined, getHeaderBorderClass(currentIndex))
                            : getHeaderBorderClass(currentIndex),
                          column.sticky ? "sticky" : "",
                          needRelative(column),
                          // 경계선: 왼쪽 스티키의 마지막이면 오른쪽 두께선, 오른쪽 스티키의 마지막이면 왼쪽 두께선
                          isLeftStickyBoundary(colIndex) ? "sticky-edge-right" : "",
                          isRightStickyBoundary(colIndex) ? "sticky-edge-left" : "",
                        ].join(" ")}
                        style={{ ...getColumnWidthStyle(column), ...stickyStyle }}
                      >
                        {column.header}
                        {resizable && column.resizable !== false && (
                          <div
                            className="resize-handle absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-[#81B334] hover:w-1 z-50 select-none"
                            onMouseDown={e => {
                              const thElement = e.currentTarget.parentElement as HTMLElement;
                              handleResizeStart(e, column.key, thElement);
                            }}
                          />
                        )}
                      </th>
                    );
                  })}
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={totalColumns} className="px-3 py-8 text-center text-gray-500">
                  로딩 중...
                </td>
              </tr>
            ) : actualData.length === 0 ? (
              <tr>
                <td colSpan={totalColumns} className="px-3 py-8 text-center text-gray-500">
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              <>
                {actualData.map((row, index) => {
                  const isSelected = selectedRows.includes(index);
                  const isRightClicked = rightClickedRowIndex === index;
                  const isClicked = highlightOnClick && clickedRowIndex === index;
                  const customRowClassName = rowClassName ? rowClassName(row, index) : "";

                  // 행별 텍스트 색상 결정
                  const customTextColor = rowTextColor ? rowTextColor(row, index) : null;

                  // 색상 우선순위: 우클릭 > 선택 > 클릭 > 사용자 정의 > 기본
                  let backgroundColor = "";
                  if (isRightClicked || isSelected || isClicked) {
                    backgroundColor = ROW_BG_SELECTED;
                  } else {
                    const customBg = getRowBgClassName?.(row, index) ?? "";
                    backgroundColor = customBg || (index % 2 === 0 ? "bg-white" : "bg-[#F6F6F6]");
                  }

                  return (
                    <tr
                      key={index}
                      className={[
                        "group",
                        "border-b border-[#EBEBEB] transition-colors",
                        backgroundColor,
                        customRowClassName,
                      ].join(" ")}
                      onClick={() => {
                        if (highlightOnClick) {
                          setClickedRowIndex(index);
                        }
                        onRowClick?.(row, index);
                      }}
                      onDoubleClick={() => {
                        onRowDoubleClick?.(row, index);
                      }}
                      onContextMenu={e => handleContextMenu(row, index, e)}
                    >
                      {showRowNumber && (
                        <td
                          className={[
                            `px-3 py-2 text-center`,
                            stickyRowNumber
                              ? getStickyClass("left", false, backgroundColor, getBodyBorderClass(0))
                              : getBodyBorderClass(0),
                            // 선택/우클릭/클릭이 아닐 때만 행 hover 색 적용
                            !(isRightClicked || isSelected || isClicked) ? ROW_BG_HOVER_TD : "",
                          ].join(" ")}
                          style={{
                            ...(stickyRowNumber ? { left: "0px", top: "0px" } : {}),
                            color: customTextColor || "#000",
                          }}
                        >
                          <div
                            className={`flex items-center justify-center whitespace-nowrap truncate w-full min-h-[1lh]`}
                          >
                            {index + 1}
                          </div>
                        </td>
                      )}
                      {showCheckbox && (
                        <td
                          className={[
                            `px-3 py-2 text-center`,
                            stickyCheckbox
                              ? getStickyClass(
                                  "left",
                                  false,
                                  backgroundColor,
                                  getBodyBorderClass(showRowNumber ? 1 : 0)
                                )
                              : getBodyBorderClass(showRowNumber ? 1 : 0),
                            // 선택/우클릭/클릭이 아닐 때만 행 hover 색 적용
                            !(isRightClicked || isSelected || isClicked) ? ROW_BG_HOVER_TD : "",
                          ].join(" ")}
                          style={
                            stickyCheckbox
                              ? {
                                  left: `${stickyRowNumber ? (rowNumThRef.current?.getBoundingClientRect().width ?? 0) : 0}px`,
                                  top: "0px",
                                }
                              : {}
                          }
                        >
                          <div
                            className="flex items-center justify-center w-full cursor-pointer min-h-[1lh]"
                            onClick={(e: React.MouseEvent) => {
                              e.stopPropagation();
                              handleRowSelect(index);
                            }}
                          >
                            <CheckBox checked={isSelected} onChange={() => handleRowSelect(index)} />
                          </div>
                        </td>
                      )}

                      {(() => {
                        let skipNext = 0; // 행마다 초기화

                        return columns.map((column, colIndex) => {
                          if (skipNext > 0) { skipNext--; return null; }

                          const currentIndex =
                            (showRowNumber ? 1 : 0) + (showCheckbox ? 1 : 0) + colIndex;
                          const stickyStyle = getStickyPosition(colIndex, column.sticky);

                          const colSpan =
                            typeof getCellColSpan === "function"
                              ? (getCellColSpan(row, column, colIndex) ?? 1)
                              : 1;

                          if (colSpan === 0) return null; // 병합으로 스킵
                          if (colSpan > 1) skipNext = colSpan - 1;

                          const widthStyle = colSpan > 1 ? {} : getColumnWidthStyle(column); // 병합셀은 width 강제 X

                          return (
                            <td
                              key={column.key}
                              colSpan={colSpan}
                              className={[
                                "px-3 py-2",
                                getBodyAlignClass(column.bodyAlign),
                                column.sticky
                                  ? getStickyClass(
                                      column.sticky,
                                      false,
                                      backgroundColor,
                                      getBodyBorderClass(currentIndex)
                                    )
                                  : getBodyBorderClass(currentIndex),
                                column.sticky ? "sticky" : "",
                                // 경계선: 왼쪽 스티키의 마지막이면 오른쪽 두께선, 오른쪽 스티키의 마지막이면 왼쪽 두께선
                                isLeftStickyBoundary(colIndex) ? "sticky-edge-right" : "",
                                isRightStickyBoundary(colIndex) ? "sticky-edge-left" : "",
                                // 선택/우클릭/클릭 상태가 아닐 때만 행 hover 색 적용
                                !(isRightClicked || isSelected || isClicked) ? ROW_BG_HOVER_TD : "",
                              ].join(" ")}
                              style={{
                                ...widthStyle,
                                ...stickyStyle,
                                color: customTextColor || "#374151", // text-gray-900 색상값
                              }}
                            >
                              <div
                                className={`flex items-center ${getAlignClasses(column.bodyAlign)} whitespace-nowrap truncate w-full [&>*]:m-0 min-h-[1lh]`}
                              >
                                {column.render ? column.render(row[column.key], row, index) : row[column.key] || ""}
                              </div>
                            </td>
                          );
                        });
                      })()}

                    </tr>
                  );
                })}
                {/* 무한 스크롤 트리거 및 로딩 표시 */}
                {infiniteScroll && (
                  <tr>
                    <td colSpan={totalColumns} className="px-3 py-4 text-center">
                      <div ref={infiniteScrollResult.loadMoreRef} className="w-full h-4">
                        {actualLoadingMore ? (
                          <div className="text-gray-500">더 많은 데이터 로딩 중...</div>
                        ) : actualHasMore ? (
                          <div className="text-gray-400 text-sm"></div>
                        ) : actualData.length > 0 ? (
                          <div className="text-gray-400 text-sm">모든 데이터를 불러왔습니다</div>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                )}
              </>
            )}
          </tbody>
        </table>
      </div>

      {/* 컨텍스트 메뉴 */}
      {contextMenu.isOpen && contextMenuItems && (
        <ContextMenu
          isOpen={contextMenu.isOpen}
          x={contextMenu.x}
          y={contextMenu.y}
          items={contextMenuItems(contextMenu.row, contextMenu.index)}
          onClose={closeContextMenu}
        />
      )}
    </div>
  );
}
